import { useEffect, useMemo, useRef, useState } from 'react';
import { collection, onSnapshot, query, where } from 'firebase/firestore';
import { db } from '../../lib/firebase';
import { useAuth } from '../../context/AuthContext';
import { subscribeLeads } from '../../data/listeners/leads';
import { csvToObjects } from '../../utils/csv';
import {
  autoMap, mapRowsToLeads, dedupeByContactId, writeLeads, IMPORT_FIELDS,
} from '../../data/commands/importLeads';
import { useAssignableAgents } from '../../hooks/useAssignableAgents';
import { useTerritories } from '../../context/TerritoriesContext';
import Icon from '../../components/Icon';

const IGNORE = '';

// CSV import (mockup §699): drop zone → Step 1 Target (store + on-duplicate) →
// Step 2 visible column mapping → Step 3 preview + import. Reuses the existing
// import functions unchanged; On-duplicate Update re-writes existing Contact IDs
// (writeLeads set()s by Contact ID), Skip writes only new ones.
export default function CsvImport() {
  const {
    tenantId, role, territoryIds, profile, authUser,
  } = useAuth();
  const [text, setText] = useState('');
  const [existingIds, setExistingIds] = useState(new Set());
  const [stores, setStores] = useState([]);
  const [territories, setTerritories] = useState([]);
  const [storeId, setStoreId] = useState(profile?.storeIds?.[0] ?? 'hub-makati');
  const [defaultOwnerUid, setDefaultOwnerUid] = useState(''); // Agent for imported rows
  const [defaultTerritoryId, setDefaultTerritoryId] = useState(''); // fallback territory
  const [onDup, setOnDup] = useState('skip'); // 'skip' | 'update'
  const [mapping, setMapping] = useState({});
  const [dragging, setDragging] = useState(false);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState('');
  const [error, setError] = useState('');
  const fileRef = useRef(null);

  useEffect(() => subscribeLeads({ tenantId, role, territoryIds }, (leads) => setExistingIds(new Set(leads.map((l) => l.id)))), [tenantId, role, territoryIds]);
  useEffect(() => {
    if (!tenantId) return undefined;
    const qs = query(collection(db, 'stores'), where('tenantId', '==', tenantId));
    return onSnapshot(qs, (snap) => setStores(snap.docs.map((d) => ({ id: d.id, ...d.data() }))), () => setStores([]));
  }, [tenantId]);
  useEffect(() => {
    if (!tenantId) return undefined;
    const qt = query(collection(db, 'territories'), where('tenantId', '==', tenantId));
    return onSnapshot(qt, (snap) => setTerritories(snap.docs.map((d) => decodeTerritory(d))), () => setTerritories([]));
  }, [tenantId]);

  const agents = useAssignableAgents();
  const { territories: terrOptions } = useTerritories();
  const actor = { uid: authUser?.uid, name: profile?.name ?? profile?.email };
  const ownerName = agents.find((a) => a.uid === defaultOwnerUid)?.name ?? null;

  const objs = useMemo(() => (text.trim() ? csvToObjects(text) : []), [text]);
  const headers = useMemo(() => (objs.length ? Object.keys(objs[0]) : []), [objs]);
  const headerSig = headers.join('|');
  // Re-seed the mapping (auto-match) whenever a different CSV's headers load.
  useEffect(() => { setMapping(headers.length ? autoMap(headers) : {}); }, [headerSig]);

  const preview = useMemo(() => {
    if (!objs.length) return null;
    const leads = mapRowsToLeads(objs, mapping, {
      tenantId, storeId, territories, ownerUid: defaultOwnerUid || null, ownerName, territoryId: defaultTerritoryId || null,
    });
    // Skip semantics: only Contact IDs not already in the store. Update semantics:
    // first occurrence of each Contact ID in the file (writeLeads overwrites).
    const skip = dedupeByContactId(leads, existingIds);
    const all = dedupeByContactId(leads, new Set());
    const toWrite = onDup === 'update' ? all.fresh : skip.fresh;
    const terrCount = new Set(toWrite.map((l) => l.territoryId).filter(Boolean)).size;
    return {
      total: objs.length, toWrite, dupes: objs.length - toWrite.length, terrCount,
    };
  }, [objs, mapping, existingIds, tenantId, storeId, onDup, territories, defaultOwnerUid, defaultTerritoryId, ownerName]);

  function loadFile(f) {
    if (!f) return;
    const reader = new FileReader();
    reader.onload = () => { setText(String(reader.result)); setResult(''); setError(''); };
    reader.readAsText(f);
  }

  async function doImport() {
    if (!preview?.toWrite.length) return;
    setError(''); setResult(''); setBusy(true);
    try {
      const n = await writeLeads(preview.toWrite, actor);
      setResult(`Imported ${n} leads (${preview.dupes} ${onDup === 'update' ? 'in-file duplicates' : 'duplicates'} skipped).`);
      setText('');
    } catch (err) {
      setError(err.message || 'Import failed.');
    } finally {
      setBusy(false);
    }
  }

  const setCol = (field) => (e) => {
    const col = e.target.value;
    setMapping((m) => {
      const next = { ...m };
      if (col === IGNORE) delete next[field];
      else next[field] = col;
      return next;
    });
  };

  return (
    <div style={{ maxWidth: 760 }}>
      <input ref={fileRef} type="file" accept=".csv,text/csv" style={{ display: 'none' }} onChange={(e) => loadFile(e.target.files?.[0])} />

      <div
        className={`dropzone${dragging ? ' dragover' : ''}`}
        style={{ marginBottom: 'var(--space-5)' }}
        onClick={() => fileRef.current?.click()}
        onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
        onDragLeave={() => setDragging(false)}
        onDrop={(e) => { e.preventDefault(); setDragging(false); loadFile(e.dataTransfer.files?.[0]); }}
      >
        <Icon name="importf" size={22} /><br />
        <strong>Drop CSV here</strong> or click to browse<br />
        <span style={{ fontSize: 'var(--text-small)' }}>First row = headers, must include Contact ID. Dedupe by Contact ID; territory auto-assigned by lat/lng.</span>
      </div>

      <div className="card card-pad" style={{ marginBottom: 'var(--space-5)' }}>
        <div className="section-title" style={{ marginTop: 0 }}>Step 1 · Target</div>
        <div className="toolbar">
          <div className="field"><label>Store</label>
            <select className="input" value={storeId} onChange={(e) => setStoreId(e.target.value)}>
              {stores.length === 0 && <option value={storeId}>Makati Hub</option>}
              {stores.map((s) => <option key={s.id} value={s.id}>{s.name ?? s.id}</option>)}
            </select>
          </div>
          <div className="field"><label>Assign to agent</label>
            <select className="input" value={defaultOwnerUid} onChange={(e) => setDefaultOwnerUid(e.target.value)}>
              <option value="">— unassigned —</option>
              {agents.map((a) => <option key={a.uid} value={a.uid}>{a.name}</option>)}
            </select>
          </div>
          <div className="field"><label>Default territory</label>
            <select className="input" value={defaultTerritoryId} onChange={(e) => setDefaultTerritoryId(e.target.value)}>
              <option value="">By lat/lng (if present)</option>
              {terrOptions.map((t) => <option key={t.id} value={t.id}>{t.label ?? t.id}</option>)}
            </select>
          </div>
          <div className="field"><label>On duplicate</label>
            <select className="input" value={onDup} onChange={(e) => setOnDup(e.target.value)}>
              <option value="skip">Skip</option>
              <option value="update">Update</option>
            </select>
          </div>
        </div>
      </div>

      <div className="card card-pad" style={{ marginBottom: 'var(--space-5)' }}>
        <div className="section-title" style={{ marginTop: 0 }}>Step 2 · Map columns</div>
        {headers.length === 0
          ? <p className="muted" style={{ fontSize: 'var(--text-small)' }}>Load a CSV to map its columns to lead fields.</p>
          : Object.entries(IMPORT_FIELDS).map(([field, label]) => (
            <div key={field} className="maprow">
              <select className="input" style={{ minWidth: 220 }} value={mapping[field] ?? IGNORE} onChange={setCol(field)}>
                <option value={IGNORE}>— ignore —</option>
                {headers.map((h) => <option key={h} value={h}>{h}</option>)}
              </select>
              <span className="arrow">→</span>
              <b>{label}{field === 'contactId' ? ' (dedupe key)' : ''}</b>
            </div>
          ))}
      </div>

      <div className="card card-pad">
        <div className="section-title" style={{ marginTop: 0 }}>Step 3 · Preview &amp; import</div>
        <div className="stat-row" style={{ marginBottom: 'var(--space-4)' }}>
          <div className="stat"><div className="n">{preview?.total ?? 0}</div><div className="l">Rows</div></div>
          <div className="stat"><div className="n" style={{ color: 'var(--presto-warning)' }}>{preview?.dupes ?? 0}</div><div className="l">Dupes ({onDup})</div></div>
          <div className="stat"><div className="n" style={{ color: 'var(--presto-success)' }}>{preview?.toWrite.length ?? 0}</div><div className="l">{onDup === 'update' ? 'To write' : 'New'}</div></div>
          <div className="stat"><div className="n">{preview?.terrCount ?? 0}</div><div className="l">Territories</div></div>
        </div>
        {error && <div className="form-error">{error}</div>}
        {result && <div style={{ color: 'var(--presto-success)', fontSize: 'var(--text-small)', marginBottom: 'var(--space-3)' }}>{result}</div>}
        <button className="btn btn-primary" disabled={busy || !preview?.toWrite.length} onClick={doImport}>
          {busy ? 'Importing…' : `Import ${preview?.toWrite.length ?? 0} leads`}
        </button>
      </div>
    </div>
  );
}

// Territories store polygon/centroid as {lat,lng} maps (DoR-27a) → decode to the
// [lat,lng] tuples assignTerritory expects.
function decodeTerritory(d) {
  const t = d.data();
  return {
    id: d.id,
    ...t,
    polygon: Array.isArray(t.polygon) ? t.polygon.map((p) => [p.lat, p.lng]) : [],
    centroid: t.centroid ? [t.centroid.lat, t.centroid.lng] : null,
  };
}
